/**
 * Configuración para el sistema de verificación de versiones
 */

export const VERSION_CONFIG = {
  // Intervalo de verificación en milisegundos (30 minutos por defecto)
  CHECK_INTERVAL: 30 * 60 * 1000,

  // Tiempo mínimo entre verificaciones (5 minutos)
  MIN_CHECK_INTERVAL: 5 * 60 * 1000,

  // Tiempo máximo para recordar que el usuario rechazó una actualización (24 horas)
  DISMISS_MEMORY_HOURS: 24,

  // Path al archivo version.json
  VERSION_JSON_PATH: '/version.json',

  // Máximo número de auto-reloads antes de mostrar modal (prevención de loops)
  MAX_AUTO_RELOADS: 3,

  // Ventana de tiempo para contar reloads (5 minutos)
  RELOAD_LOOP_WINDOW_MS: 5 * 60 * 1000,

  // Claves de sessionStorage para el sistema de versiones
  STORAGE_KEYS: {
    RELOAD_TIMESTAMPS: 'scae_reload_timestamps',
    LAST_KNOWN_VERSION: 'scae_last_known_version',
    STALE_DETECTED: 'scae_stale_detected',
  },

  // Configuración de Sentry para versiones
  SENTRY_CONFIG: {
    // Tags que se agregan automáticamente
    DEFAULT_TAGS: {
      feature: 'version_check',
      app_version: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'unknown',
    },

    // Niveles de log para diferentes eventos
    LOG_LEVELS: {
      VERSION_CHECK_SUCCESS: 'info',
      VERSION_OUTDATED: 'warning',
      VERSION_FORCE_UPDATE: 'error',
      VERSION_CHECK_ERROR: 'error',
      USER_UPDATE_ACTION: 'info',
      USER_DISMISS_ACTION: 'info',
    },
  },

  // Configuración de componentes
  COMPONENT_TRACKING: {
    // Componentes que se trackean automáticamente
    TRACKED_COMPONENTS: [
      'MobileEmployeePhotoLoader',
      'EmployeePhotoLoader',
      'ModalVersionUpdate',
    ],

    // Eventos que se trackean
    TRACKED_EVENTS: [
      'component_mounted',
      'component_unmounted',
      'photo_captured',
      'photo_validation_failed',
      'hardware_check',
      'version_check',
      'user_action',
    ],
  },
};

/**
 * Obtiene la configuración del entorno
 */
export const getEnvironmentConfig = () => {
  const env = import.meta.env.VITE_ENVIRONMENT || 'development';

  const configs = {
    development: {
      CHECK_INTERVAL: 5 * 60 * 1000, // 5 minutos en desarrollo
      MIN_HIDDEN_DURATION_MS: 1 * 60 * 1000, // 1 minuto para verificar al volver
      ENABLE_DEBUG_LOGS: true,
      FORCE_UPDATE_SIMULATION: false,
    },
    staging: {
      CHECK_INTERVAL: 15 * 60 * 1000, // 15 minutos en staging
      MIN_HIDDEN_DURATION_MS: 3 * 60 * 1000, // 3 minutos para verificar al volver
      ENABLE_DEBUG_LOGS: true,
      FORCE_UPDATE_SIMULATION: false,
    },
    production: {
      CHECK_INTERVAL: 30 * 60 * 1000, // 30 minutos en producción
      MIN_HIDDEN_DURATION_MS: 5 * 60 * 1000, // 5 minutos para verificar al volver
      ENABLE_DEBUG_LOGS: false,
      FORCE_UPDATE_SIMULATION: false,
    },
  };

  return {
    ...VERSION_CONFIG,
    ...configs[env],
    ENVIRONMENT: env,
  };
};

/**
 * Obtiene información del build actual
 */
export const getBuildInfo = () => ({
  version: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'unknown',
  environment: import.meta.env.VITE_ENVIRONMENT || 'development',
  build_time: typeof __BUILD_TIMESTAMP__ !== 'undefined' ? __BUILD_TIMESTAMP__ : new Date().toISOString(),
  sentry_release: import.meta.env.VITE_SENTRY_RELEASE || `scae-frontend@${typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'unknown'}`,
  git_commit: typeof __GIT_COMMIT__ !== 'undefined' ? __GIT_COMMIT__ : 'unknown',
  user_agent: navigator.userAgent,
  platform: navigator.platform,
  language: navigator.language,
  screen_resolution: `${screen.width}x${screen.height}`,
  viewport: `${window.innerWidth}x${window.innerHeight}`,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
});
